import { Component, HostListener, OnInit } from '@angular/core';
import { CommunicationService } from 'app/_services/communication.service';
import { AppComponent } from 'app/app.component';
import { UtilService } from 'app/_services/util.service';
import { LoginService } from 'app/_services/login.service';
import { IAction } from 'app/_models/IAction';
import { ISelectedLine } from 'app/_models/ISelectedLine';
import { IGanttTask } from 'app/_models/IGanttTask';
import { IHeader } from 'app/_models/IHeader';
import { ISourceViewHeader } from 'app/_models/ISourceViewHeader';
import { InactivityService } from 'app/_services/inactivity.service';
import { IFilter } from 'app/_models/IFilter';
import { ILine } from 'app/_models/ILine';
import { ISettings } from 'app/_models/ISettings';
import { IconDefinition, faBell, faXmark, faCircle } from '@fortawesome/free-solid-svg-icons';
import { ITranslations } from 'app/_models/ITranslations';
import { INavigationItem } from 'app/_models/INavigationItem';
import { RADService } from 'app/_services/rad.service';

@Component({
  selector: 'app-base',
  templateUrl: './base.component.html',
  styleUrls: ['./base.component.css']
})
export class BaseComponent implements OnInit {
  //#region Dialog Fields
  showNotifications: boolean = false;
  showSettings: boolean = false;
  feedbackMessage: string = undefined;
  imageToExpand: string = undefined;
  headersToEdit: IHeader[] = undefined;
  showImportFileDialog: boolean = false;
  showAdvancedFilters: boolean = false;
  showExportDialog: boolean = false;
  action: IAction = undefined;
  showStorageManagement: boolean = false;
  //#endregion
  //#region Misc Fields
  nodeId: string;
  userId: string;
  username: string;
  userRole: string;
  greeting: string;
  translationsLoaded: boolean = false;
  mobileView: boolean = false;
  hasUnreadNotifications: boolean = false;
  isUserMenuCollapsed: boolean = true;
  ganttView: boolean = false;
  gridView: boolean = false;
  mapView: boolean = false;
  keyValue: string;
  viewName: string;
  detailViewName: string;
  headers: IHeader[] | ISourceViewHeader[] = [];
  lines: ILine[] = [];
  filters: IFilter[] = [];
  filterString: string;
  selectedLines: ISelectedLine[] | IGanttTask[] = [];
  serverDateFormat: string;
  settings: ISettings;
  translations: ITranslations;
  openMenu: INavigationItem;
  dashboardCarouselList: any[] = [];
  mainGridName: string;
  //#endregion  
  //#region Icons
  faBell: IconDefinition = faBell;
  faCircle: IconDefinition = faCircle;
  faXmark: IconDefinition = faXmark;
  //#endregion

  constructor(
    public app: AppComponent,
    private communicationService: CommunicationService,    
    private utilService: UtilService,
    private loginService: LoginService,
    private inactivityService: InactivityService, 
    private radService: RADService   
  ) {    
    this.checkScreen();    
    this.validateAndSetupUser();     

    this.communicationService.menu_base_loadGridView$.subscribe((data: {nodeId: string, viewName: string}) => {
      this.nodeId = data.nodeId;  
      this.mainGridName = data.viewName;       
      this.ganttView = false;
      this.mapView = false;
      if (this.mobileView) this.settings.menuToggleSetting = false;
      if (this.gridView) {
        this.communicationService.loadGrid(this.nodeId);        
        return;
      }

      this.gridView = true;
    })

    this.communicationService.menu_base_loadGanttView$.subscribe((nodeId) => {
      this.nodeId = nodeId;
      this.gridView = false;
      this.mapView = false;
      if (this.ganttView) {
        this.communicationService.loadGantt(this.nodeId);
        return;
      }

      this.ganttView = true;
    })

    this.communicationService.menu_base_loadMapView$.subscribe((nodeId) => {
      this.nodeId = nodeId;
      this.ganttView = false;
      this.gridView = false;
      if (this.mobileView) this.settings.menuToggleSetting = false;
      if (this.mapView) {
        this.communicationService.loadMap(this.nodeId);
        return;    
      } 
      this.mapView = true;
    })

    this.communicationService.action_base_refresh$.subscribe(() => {
      sessionStorage.removeItem('RadLoaded');
      this.action = undefined;
      this.headers = [];
      this.selectedLines = [];
      if (this.gridView) {
        this.communicationService.loadGrid(this.nodeId);
        return;
      }

      if (this.ganttView) {
        this.communicationService.loadGantt(this.nodeId);
        return;
      }

      if (this.mapView) {
        this.communicationService.loadMap(this.nodeId);
        return;
      }
    });

    this.communicationService.action_base_closeDialog$.subscribe(() => {
      this.closeDialogs();      
    });

    this.communicationService.rad_base_showActionDialog$.subscribe(data => {
      this.communicationService.collapseRadTabs();
      this.headers = data.headers;
      this.selectedLines = data.selectedItems;
      this.action = data.action;
      this.serverDateFormat = data.serverDateFormat;
    })

    this.communicationService.grid_or_detailgrid_base_showImageDialog$.subscribe(data => {
      this.communicationService.collapseRadTabs();
      this.imageToExpand = data;
    });

    this.communicationService.usermenu_base_showSettingsDialog$.subscribe(() => {
      this.communicationService.collapseRadTabs();
      this.showSettings = true;
    });

    this.communicationService.action_base_showFeedbackDialog$.subscribe(data => {
      this.communicationService.collapseRadTabs();
      this.feedbackMessage = data;
    });

    this.communicationService.feedback_base_closeFeedbackDialog$.subscribe(() => {
      this.feedbackMessage = undefined;
    });

    this.communicationService.grid_or_detailgrid_base_showColumnDialog$.subscribe(data => {
      this.communicationService.collapseRadTabs();
      this.headersToEdit = data.headers;
      this.viewName = data.viewName;
    });

    this.communicationService.app_base_or_login_translationsLoaded$.subscribe(() => {
      this.translations = this.app.translations;
      this.checkLoadedFromDirectLink();
      this.translationsLoaded = true;      
    });

    this.communicationService.usermenu_base_closeUserMenu$.subscribe(loggingOut => {
      this.isUserMenuCollapsed = true;
      if (loggingOut) this.logout();
    });

    this.communicationService.usermenu_base_showImportFileDialog$.subscribe(() => {
      this.communicationService.collapseRadTabs();
      this.showImportFileDialog = true;
    });

    this.communicationService.usermenu_base_showNotificationsDialog$.subscribe(() => {
      this.communicationService.collapseRadTabs();
      this.openNotifications();
    });

    this.communicationService.grid_or_detailgrid_base_advancedFilterDialog$.subscribe(data => {
      this.communicationService.collapseRadTabs();
      this.headers = data.headers;
      this.filters = data.filters;
      this.filterString = data.filterString;
      this.detailViewName = data.detailViewName;
      this.serverDateFormat = data.serverDateFormat;
      this.showAdvancedFilters = true;
    });

    this.communicationService.grid_or_detailgrid_base_showExportDialog$.subscribe(data => {
      this.communicationService.collapseRadTabs();
      this.headers = data.headers;
      this.lines = data.lines;
      this.keyValue = data.keyValue;
      this.viewName = data.viewName;
      this.filterString = data.filterString;
      this.showExportDialog = true;
    });

    this.communicationService.settings_storage_showStorageManagementDialog$.subscribe(data => {
      this.communicationService.collapseRadTabs();
      this.showStorageManagement = true;
    });

    this.communicationService.settings_storage_closeStorageManagementDialog$.subscribe(() => {
      this.showStorageManagement = false;
    });

    this.communicationService.menu_base_updateOpenMenu$.subscribe((data) => {
      if (!this.openMenu) {
        this.openMenu = data;
        return;
      }      

      this.openMenu.selected = false;
      this.openMenu = data;      
    });
  }

  ngOnInit(): void {
    this.settings = this.utilService.getSettings();      
  }

  validateAndSetupUser(): void {
    if (!localStorage.getItem('qx_userId') || !localStorage.getItem('qx_username')) {
      window.location.href = '/signin';
      return;
    }

    this.userId = localStorage.getItem('qx_userId');
    this.userRole = localStorage.getItem('qx_userRole');
    this.username = localStorage.getItem('qx_username');    
    this.getDashboardCarouselList(this.userId);

    let formData = this.app.buildForm(['userId', 'username'], [this.userId, this.username]);
    this.loginService.validateLoginInformation(formData).subscribe({
      next: (data: { result: boolean, notificationCount: boolean }) => {
        if (!data.result) {
          localStorage.removeItem('qx_userId');
          localStorage.removeItem('qx_username');
          window.location.href = '/signin';
          return;
        }

        if (data.notificationCount) this.hasUnreadNotifications = true;        
        this.inactivityService.startSessionValidation();
      }, error: (errorLog) => { console.log(errorLog); }
    })
    
    this.username = this.username.replace(this.username.charAt(0), this.username.charAt(0).toUpperCase());
    if (sessionStorage.getItem('resetPassword') != undefined) {
      this.showSettings = true;
      sessionStorage.removeItem('resetPassword');
    }
  }

  checkLoadedFromDirectLink(): void {
    if (!localStorage.getItem('qx_direct') && !localStorage.getItem('qx_directFromExisting')) return;  
    
    let nodeId = '';
    if (localStorage.getItem('qx_directFromExisting')) {
      nodeId = JSON.parse(localStorage.getItem('qx_directFromExisting')).nodeId;
      this.communicationService.reloadMenu(nodeId);
    }

    if (localStorage.getItem('qx_direct')) {
      nodeId = JSON.parse(localStorage.getItem('qx_direct')).nodeId;
    }

    let formData = this.app.buildForm(['nodeId', 'userId'], [nodeId, this.userId]);
    this.utilService.getViewTypeUserAccess(formData).subscribe({
      next: (data: { accessResult: boolean, viewType: string }) => {
        if (!data.accessResult) {
          this.app.alertError(this.app.translations.ALERT_Error_ViewInaccessible);
          return;
        }

        this.app.showLoader();
        if (data.viewType == 'MasterDetailView') this.communicationService.loadGridView(nodeId, '');
        if (data.viewType == 'GanttView') this.communicationService.loadGanttView(nodeId);
        if (data.viewType == 'MapView') this.communicationService.loadMapView(nodeId);                  
      }, error: (errorLog) => { console.log(errorLog); }
    })    
  }

  consentToStorage(): void {
    this.settings.storageConsent = true;
    localStorage.setItem('qx_storageConsent', 'true');
  }

  logout(): void {
    localStorage.removeItem('qx_msconfig');
    this.inactivityService.stopMonitoring();
    this.inactivityService.terminateSession();
    sessionStorage.clear();
    localStorage.removeItem('qx_userId');
    localStorage.removeItem('qx_username');

    //remove ms acc data
    for (let i = 0; i < localStorage.length; i++) {      
      if (localStorage.key(i) && localStorage.key(i).includes('msal')) 
        localStorage.removeItem(localStorage.key(i));      
    }
    
    window.location.href = '/signin';
  }

  openUserMenu(): void {
    this.communicationService.collapseRadTabs();
    this.translationsLoaded = true;
    this.isUserMenuCollapsed = !this.isUserMenuCollapsed;
  }

  openNotifications(): void {
    this.communicationService.collapseRadTabs();
    this.showNotifications = true;
    this.hasUnreadNotifications = false;
  }  

  openStoragePolicy(): void {
    this.communicationService.closeUserMenu(false);
    window.open('/assets/cookies.html');
  }

  closeDialogs(): void {
    this.action = undefined;
    this.headers = [];
    this.selectedLines = [];
    this.imageToExpand = undefined;
    this.showSettings = false;
    this.showNotifications = false;
    this.showImportFileDialog = false;
    this.showAdvancedFilters = false;
    this.showExportDialog = false;
    this.headersToEdit = undefined;
  }

  getLocalStorageSize(): void {
    let total = 0;
    for (let key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        const value = localStorage.getItem(key);
        total += key.length + value.length;
      }
    }

    console.log(`Estimated localStorage usage: ${((total * 2) / 1024).toFixed(2)} KB`);    
  }

  getDashboardCarouselList(userId: string): void {
    let formData = this.app.buildForm(['userId'], [userId]);
    this.radService.getDashboardCarouselList(formData).subscribe({
      next: (data: any) => { 
        this.dashboardCarouselList =  data.result ? data.result : [];        
      }, error: (errorLog) => { console.log(errorLog); }
    });
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    if (event.key === 'Escape' || event.key === 'Esc') this.closeDialogs();
  }

  @HostListener('window:resize')
  onResize() {
    this.checkScreen();
  }

  checkScreen() {
    this.mobileView = window.innerWidth <= 500;
  }
}